// src/utils/leadRule.js
//
// THE lead rule, for the browser (docs/features/one_lead_rule_FEATURE.md; Erik, 2026-10-09: "Every part of the site
// should be using the same rule."). The server copy is functions-shared/utils/leadPopulation.js; the two are pinned to
// each other by scripts/verify-one-lead-rule.mjs.
//
// A lead is a customer the journey rule stamped `system.journey.section == 'lead'`
// (functions-field/journey/journeySummaryRule.js): no signed contract; not merged, canceled, dead, finished or hidden;
// not a build partner's customer. Read the stamp; never re-derive it from the pipeline, the stage or a status.

export const LEAD_SECTION = 'lead'

/** The stamp says this customer is a lead. */
export function isLead(customer) {
  return customer?.system?.journey?.section === LEAD_SECTION
}

/**
 * For a record that may be seconds old (a pool lead just created, before the journey trigger stamps it): a lead unless
 * the stamp says otherwise. The Lead Pool uses this; everything else uses isLead.
 */
export function isLeadOrUnstamped(customer) {
  const section = customer?.system?.journey?.section
  return section === undefined || section === null || section === LEAD_SECTION
}

/**
 * Past the lead stage, by the stamp: the contract is signed, or the customer is a build partner's. The base status is
 * 'Active' on this side and 'Lead' (or a lead sub-status) otherwise (the server's leadPopulation.isSignedSide).
 */
export function isSignedSide(customer) {
  const journey = customer?.system?.journey
  return (
    journey?.partnerOfferPending !== true &&
    (journey?.contractSigned === true || journey?.buildPartner === true)
  )
}

/** Build-partner routing tag; pending 1Earth offers can also be leads. Read section for lead identity. */
export function isBuildPartnerCustomer(customer) {
  return customer?.system?.journey?.buildPartner === true
}

// The journey's five sections, as a person reads them (the Command Board's vocabulary).
const SECTION_LABELS = Object.freeze({
  lead: 'Lead',
  preInstall: 'Pre-install',
  postInstall: 'Post-install',
  maintenance: 'Maintenance',
  closed: 'Closed',
})

/** "Lead", "Pre-install", "Post-install", "Maintenance" or "Closed", from the stamp's section. */
export function journeySectionLabel(section) {
  return SECTION_LABELS[section] || 'Not stamped'
}

/**
 * The four segments every sales screen counts, from the stamp (the Sales Command Center, the Org and Admin dashboards
 * and their drilldowns): 'lead'; 'active' (signed or a build partner's job, not finished: pre-install or post-install);
 * 'complete' (maintenance); 'canceled' (closed as canceled, dead or merged). A hidden (archived) record is in none:
 * null. A record not stamped yet is seconds old: a lead.
 */
export function salesSegmentOf(customer) {
  const journey = customer?.system?.journey
  const section = journey?.section
  if (section === undefined || section === null || section === LEAD_SECTION) return 'lead'
  if (section === 'preInstall' || section === 'postInstall') return 'active'
  if (section === 'maintenance') return 'complete'
  if (section === 'closed') return journey.closedReason === 'hidden' ? null : 'canceled'
  return null
}
